<template>
    <div class="ble-history-chart-view">
        <div class="ble-history-chart-view__datepicker">
            <DateRangeInput v-model="customRange" />
        </div>

        <div class="ble-history-chart-view__chart">
            <div v-if="isLoading" class="l-stack l-center l-padded">
                <VSpinner size="medium" line-fg-color="#000" :speed="1" />
            </div>

            <ApexChart
                v-else-if="series.length"
                ref="chart"
                height="100%"
                :options="chartOptions"
                :series="series"
            />
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import ApexChart from 'vue-apexcharts'
import VSpinner from 'vue-simple-spinner'

import { formatHelper, httpHelper } from '@/utils'
import ChartHelper from '@/mixins/ChartHelper'
import DateRangeInput from '@/components/DateRangeInput'
import UrlHelper from '@/mixins/UrlHelper'

export default {
    name: 'BleHistoryChartView',
    components: {
        ApexChart,
        DateRangeInput,
        VSpinner,
    },
    mixins: [ChartHelper, UrlHelper],
    data() {
        return {
            series: [],
            chartOptions: {
                chart: {
                    type: 'rangeBar',
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        rangeBarGroupRows: true,
                    },
                },
                stroke: {
                    width: [0, 0, 2],
                },
                dataLabels: {
                    enabled: true,
                    formatter: (_, { dataPointIndex, seriesIndex }) => {
                        const { duration, finished } = this.series[
                            seriesIndex
                        ].data[dataPointIndex]
                        const durationFormatted =
                            duration < 60
                                ? `${Math.floor(duration)}s`
                                : formatHelper.hoursAndMinutesDuration(duration)
                        return `${durationFormatted} ${
                            finished === false ? ' ...' : ''
                        }`
                    },
                },
                tooltip: {
                    x: {
                        format: 'dd.MM.yyyy HH:mm:ss',
                    },
                },
                xaxis: {
                    type: 'datetime',
                    labels: {
                        datetimeFormatter: {
                            year: 'yyyy',
                            month: "MMM 'yy",
                            day: 'dd MMM',
                            hour: 'HH:mm',
                        },
                    },
                },
                yaxis: {
                    showForNullSeries: false,
                    min: min => {
                        return Math.max(
                            this.customRange.startDate.getTime(),
                            min
                        )
                    },
                    max: max => {
                        return Math.min(this.customRange.endDate.getTime(), max)
                    },
                },
                legend: {
                    showForSingleSeries: true,
                },
            },
            customRange: {
                startDate: moment()
                    .startOf('day')
                    .toDate(),
                endDate: moment()
                    .endOf('day')
                    .toDate(),
            },
            isLoading: false,
        }
    },
    computed: {
        ...mapState('tracker', ['trackers']),
        tracker() {
            return this.trackers.find(({ id }) => id == this.$route.params.id)
        },
    },
    watch: {
        customRange() {
            this.load()
        },
    },
    created() {
        moment.locale(this.$i18n.locale)
    },
    mounted() {
        this.keepAsQueryParams(true, {
            'customRange.startDate': {
                key: 'start',
                type: 'date',
            },
            'customRange.endDate': {
                key: 'end',
                type: 'date',
            },
        })

        this.chartOptions.colors = [
            this.chartOptions.colorsDefault[0],
            this.chartOptions.colorsDefault[1],
            this.chartOptions.colorsDefault[0],
        ]
    },
    methods: {
        async load() {
            this.isLoading = true
            const { data } = await httpHelper.getRecursively(
                '/ble/beacon-history/',
                {
                    params: {
                        limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
                        start: moment(this.customRange.startDate).format(),
                        end: moment(this.customRange.endDate).format(),
                        ...(this.tracker.is_ble_gateway
                            ? { gateway: this.tracker.asset }
                            : { beacon: this.tracker.asset }),
                    },
                }
            )
            this.setData(data.results)
            this.isLoading = false
        },
        setData(results) {
            const punctualSeries = []

            const inReachSeries = results.reduce((acc, cur) => {
                const x = this.tracker.is_ble_gateway
                    ? cur.beacon.name
                    : cur.gateway.name
                const y1 = new Date(cur.first_seen).getTime()
                const y2 = new Date(cur.last_seen).getTime()
                if (!cur.duration) {
                    punctualSeries.push({ x, y: y1 })
                }
                return acc.concat({
                    x,
                    y: [y1, y2],
                    duration: cur.duration,
                    finished: cur.finished,
                    ...(!cur.finished && {
                        fillColor: this.chartOptions.colors[0] + '80',
                    }),
                })
            }, [])

            const outOfReachSeries = inReachSeries.reduce((acc, cur, i) => {
                const next = inReachSeries
                    .slice(i + 1)
                    .find(({ x }) => x === cur.x)
                return next
                    ? acc.concat({
                          x: cur.x,
                          y: [cur.y[1], next.y[0]],
                          duration: (next.y[0] - cur.y[1]) / 1000,
                      })
                    : acc
            }, [])

            const bleAsset = this.tracker.is_ble_gateway ? 'Beacon' : 'Gateway'
            this.series = [
                {
                    name: this.$t('inReach', { bleAsset }),
                    data: inReachSeries,
                },
                {
                    name: this.$t('outOfReach', { bleAsset }),
                    data: outOfReachSeries,
                },
                {
                    name: '',
                    data: outOfReachSeries.length ? punctualSeries : [],
                },
            ]

            this.$nextTick(() => {
                this.$refs.chart.zoomX(
                    this.customRange.startDate.getTime(),
                    this.customRange.endDate.getTime()
                )
            })
        },
    },
}
</script>

<i18n>
{
    "en": {
        "inReach": "{bleAsset} within reach",
        "outOfReach": "{bleAsset} no longer within reach"
    },
    "de": {
        "inReach": "{bleAsset} in Reichweite",
        "outOfReach": "{bleAsset} nicht mehr in Reichweite"
    },
    "fr": {
        "inReach": "{bleAsset} à portée",
        "outOfReach": "{bleAsset} plus à portée"
    },
    "it": {
        "inReach": "{bleAsset} a portata di mano",
        "outOfReach": "{bleAsset} non più a portata di mano"
    }
}
</i18n>

<style lang="scss">
.ble-history-chart-view {
    display: flex;

    &__chart {
        flex-grow: 100;
        margin: 1rem 1rem 1rem 0;
        border-left: $style-border;

        .apexcharts-legend {
            & > * {
                &:nth-child(3) {
                    display: none;
                }
            }
        }
    }

    &__datepicker {
        padding: 1rem;
        width: 20%;
        min-width: 350px;
    }

    @include respond-to('for-tablet-down') {
        display: block;

        &__chart {
            border: none;
        }

        &__datepicker {
            padding: 0 0 1rem;
            margin: 1rem 1rem 0;
            border-bottom: $style-border;
        }
    }
}
</style>
